import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { FormDialog } from "@/components/kids/kidsUi";
import { AMBER } from "./listsUi";

const NOT_READABLE = "We couldn't read that page. Paste the recipe text instead.";

export default function RecipeDialog({ base, onClose, onAdd }) {
  const [text, setText] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const read = async () => {
    const v = text.trim();
    if (!v) return;
    setState("reading");
    setError("");
    try {
      const body = /^https?:\/\/\S+$/i.test(v) ? { url: v } : { text: v };
      const { data } = await api.post(`${base}/recipe`, body, { timeout: 35000 });
      setRows(data.ingredients.map((r) => ({ ...r, on: true })));
      setState("list");
    } catch (e) {
      setError(e.response ? errMsg(e) : NOT_READABLE);
      setState("idle");
    }
  };
  const picked = rows.filter((r) => r.on);
  return (
    <FormDialog open onClose={onClose} title="Add from recipe" testid="recipe-dialog">
      {state !== "list" ? (
        <div className="space-y-4">
          <textarea className="q-input min-h-[160px] resize-y" placeholder="Paste the recipe text, or a link to a recipe page" value={text}
            onChange={(e) => { setText(e.target.value); setError(""); }} disabled={state === "reading"} data-testid="recipe-input" />
          {state === "reading" && <p className="flex items-center gap-2 text-sm font-semibold" style={{ color: `rgb(${AMBER})` }} data-testid="recipe-reading"><Loader2 className="h-4 w-4 animate-spin" /> Reading the recipe…</p>}
          {error && <p className="q-error" data-testid="recipe-error">{error}</p>}
          <div className="flex justify-end gap-2">
            <button className="q-btn q-btn-ghost" onClick={onClose} data-testid="recipe-cancel-btn">Cancel</button>
            <button className="q-btn q-btn-primary" disabled={!text.trim() || state === "reading"} onClick={read} data-testid="recipe-read-btn">Get ingredients</button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-q-muted">Untick anything you already have.</p>
          <ul className="max-h-[50vh] space-y-1 overflow-y-auto" data-testid="recipe-ingredients">
            {rows.map((r, i) => (
              <li key={`${r.name}-${i}`}>
                <button type="button" role="checkbox" aria-checked={r.on} onClick={() => setRows(rows.map((x, j) => (j === i ? { ...x, on: !x.on } : x)))}
                  className="flex min-h-[44px] w-full items-center gap-3 rounded-lg px-2 text-left hover:bg-q-raised" data-testid={`recipe-ing-${i}`} data-name={r.name} data-on={r.on ? "1" : "0"}>
                  <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border-2" style={{ borderColor: r.on ? `rgb(${AMBER})` : "rgb(var(--q-line))", background: r.on ? `rgb(${AMBER})` : "transparent" }}>
                    {r.on && <Check className="h-4 w-4" style={{ color: "#1a1204" }} strokeWidth={3} />}
                  </span>
                  <span className="min-w-0 flex-1 font-semibold text-q-text">{r.name}</span>
                  {r.qty && <span className="text-xs text-q-muted" data-testid={`recipe-ing-qty-${i}`}>{r.qty}</span>}
                </button>
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-2">
            <button className="q-btn q-btn-ghost" onClick={() => setState("idle")} data-testid="recipe-back-btn">Back</button>
            <button className="q-btn q-btn-primary" disabled={!picked.length} onClick={() => onAdd(picked.map(({ name, qty }) => ({ name, qty })))} data-testid="recipe-add-btn">Add {picked.length} item{picked.length === 1 ? "" : "s"}</button>
          </div>
        </div>
      )}
    </FormDialog>
  );
}
